import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Collapse,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Assignment as JobIcon,
  CheckCircle as CompletedIcon,
  Error as FailedIcon,
  HourglassEmpty as ProcessingIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { adminApi, OverviewStats } from '../services/api';
import { jobTypeLabel } from '../services/labels';

const COLORS = ['#38a169', '#e53e3e', '#dd6b20', '#3182ce'];

interface StatCardProps {
  title: string;
  value: number | string | null;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={700} color={color}>
            {value ?? '-'}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: `${color}15`,
            color: color,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [copiedJobId, setCopiedJobId] = useState<string>('');

  // Helper function to format milliseconds to human-readable duration
  const formatDuration = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      const remainingSeconds = seconds % 60;
      return `${hours}h ${remainingMinutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await adminApi.getOverviewStats({
          recentJobsOffset: page * rowsPerPage,
          recentJobsLimit: rowsPerPage
        });
        if (response.success) {
          setStats(response.data);
        } else {
          setError('Failed to load statistics');
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [page, rowsPerPage]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const statusData = stats?.jobsByStatus ? [
    { name: 'Completed', value: stats.jobsByStatus.COMPLETED || 0 },
    { name: 'Failed', value: stats.jobsByStatus.FAILED || 0 },
    { name: 'Processing', value: stats.jobsByStatus.PROCESSING || 0 },
    { name: 'Queued', value: stats.jobsByStatus.QUEUED || 0 },
  ].filter(d => d.value > 0) : [];

  const jobTypeData = (stats?.topJobTypes?.slice(0, 8) || []).map(d => ({ ...d, type: jobTypeLabel(d.type) }));

  const toggleRowExpansion = (jobId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  const copyJobIdToClipboard = async (jobId: string) => {
    try {
      await navigator.clipboard.writeText(jobId);
      setCopiedJobId(jobId);
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Failed to copy job ID:', err);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Dashboard
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Overview for period:
          </Typography>
          <Chip 
            label={stats?.period || '-'} 
            size="small" 
            color="primary" 
            variant="outlined" 
          />
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Users"
            value={stats?.totalUsers ?? null}
            icon={<PeopleIcon />}
            color="#3182ce"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Organizations"
            value={stats?.totalOrganizations ?? null}
            icon={<BusinessIcon />}
            color="#805ad5"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Jobs Today"
            value={stats?.jobsToday ?? null}
            icon={<JobIcon />}
            color="#38a169"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Cost Today"
            value={stats?.totalCostToday !== undefined ? `$${stats.totalCostToday.toFixed(2)}` : null}
            icon={<JobIcon />}
            color="#e53e3e"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Currently Processing"
            value={stats?.activeProcessing ?? null}
            icon={<ProcessingIcon />}
            color="#dd6b20"
          />
        </Grid>
      </Grid>

      {/* Job Status Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CompletedIcon sx={{ color: 'success.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {stats?.jobsByStatus?.COMPLETED || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Completed Today
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FailedIcon sx={{ color: 'error.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {stats?.jobsByStatus?.FAILED || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Failed Today
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ProcessingIcon sx={{ color: 'warning.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {stats?.jobsByStatus?.PROCESSING || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Processing
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <JobIcon sx={{ color: 'info.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {stats?.jobsByStatus?.QUEUED || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Queued
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Daily Cost Trend - Last 30 Days */}
      {stats?.dailyCosts && stats.dailyCosts.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Cost Trend (Last 30 Days)
              </Typography>
              <Chip 
                label={`Total: $${stats.dailyCosts.reduce((sum, day) => sum + day.cost, 0).toFixed(2)}`}
                size="medium"
                sx={{ 
                  backgroundColor: '#e53e3e15',
                  color: '#e53e3e',
                  fontWeight: 600,
                  fontSize: '0.9rem'
                }}
              />
            </Box>
            <Box sx={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.dailyCosts}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => `$${value.toFixed(2)}`}
                  />
                  <RechartsTooltip 
                    formatter={(value: number) => [`$${value.toFixed(2)}`, 'Cost']}
                    labelFormatter={(date: string) => new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="cost" 
                    stroke="#e53e3e" 
                    strokeWidth={2}
                    dot={{ fill: '#e53e3e', r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Top Job Types
              </Typography>
              <Box sx={{ height: 300 }}>
                {jobTypeData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={jobTypeData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="type" type="category" width={150} tick={{ fontSize: 12 }} />
                      <RechartsTooltip />
                      <Bar dataKey="count" fill="#3182ce" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="text.secondary">No job data available</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Job Status Distribution
              </Typography>
              <Box sx={{ height: 300 }}>
                {statusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {statusData.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="text.secondary">No status data available</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Recent Jobs Table */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Recent Activity (Latest Runs)
          </Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: 'action.hover' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: 50 }}></TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Job ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Processing Time</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Input Tokens</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Output Tokens</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats?.recentJobs && stats.recentJobs.length > 0 ? (
                  stats.recentJobs.map((job) => (
                    <React.Fragment key={job.jobId}>
                      <TableRow hover sx={{ '& > *': { borderBottom: expandedRows.has(job.jobId) ? 'none' : undefined } }}>
                        <TableCell>
                          {job.status === 'FAILED' && job.errorMessage ? (
                            <IconButton
                              size="small"
                              onClick={() => toggleRowExpansion(job.jobId)}
                              sx={{ padding: '4px' }}
                            >
                              {expandedRows.has(job.jobId) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          ) : null}
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            fontSize: '0.8rem', 
                            fontFamily: 'monospace',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          <Tooltip title="Click to copy full Job ID" arrow>
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.5,
                              }}
                              onClick={() => copyJobIdToClipboard(job.jobId)}
                            >
                              <span>{job.jobId.substring(0, 8)}...</span>
                              <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {job.userEmail || job.userId.substring(0, 8)}
                        </TableCell>
                        <TableCell>{jobTypeLabel(job.jobType)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={job.status} 
                            size="small"
                            color={
                              job.status === 'COMPLETED' ? 'success' : 
                              job.status === 'FAILED' ? 'error' : 
                              job.status === 'PROCESSING' ? 'warning' : 'default'
                            }
                            variant="outlined"
                            sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {job.processingTimeMs ? formatDuration(job.processingTimeMs) : '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', textAlign: 'right', fontFamily: 'monospace' }}>
                          {job.inputTokens ? job.inputTokens.toLocaleString() : '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', textAlign: 'right', fontFamily: 'monospace' }}>
                          {job.outputTokens ? job.outputTokens.toLocaleString() : '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {new Date(job.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                          {job.costUsd > 0 ? `$${job.costUsd.toFixed(4)}` : '$0.0000'}
                        </TableCell>
                      </TableRow>
                      {job.status === 'FAILED' && job.errorMessage && (
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                            <Collapse in={expandedRows.has(job.jobId)} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 2, p: 2, backgroundColor: 'error.light', borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
                                <Typography variant="subtitle2" fontWeight={600} color="error.dark" gutterBottom>
                                  Error Details
                                </Typography>
                                <Typography variant="body2" color="error.dark" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                  {job.errorMessage}
                                </Typography>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">No recent jobs found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={stats?.totalRecentJobs || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
            sx={{ borderTop: 1, borderColor: 'divider' }}
          />
        </CardContent>
      </Card>

      {/* Generated timestamp */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'right' }}>
        Last updated: {stats?.generatedAt ? new Date(stats.generatedAt).toLocaleString() : '-'}
      </Typography>

      {/* Snackbar for copy confirmation */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        message={`Job ID copied: ${copiedJobId.substring(0, 12)}...`}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default DashboardPage;

